import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { BlockThemeConfig, Service } from 'webapp-types';
import type { BlockVisualizationSpecial } from 'webapp/enums/enums';
import {
	BlockVisualization,
	ShownLabels,
	SortFieldType,
	TableColumnType,
} from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import type Block from 'webapp/models-custom/block';
import type { RenderChangesFromData } from 'webapp/models-custom/block-renderer';
import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputBase from 'webapp/models-custom/ds-data/output/base';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import type DsDataPointBase from 'webapp/models-custom/ds-data/point/base';
import Serviced from 'webapp/models-custom/serviced';
import StatusObject from 'webapp/models-custom/status-object';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationBubble from 'webapp/models-custom/visualization/bubble';
import type VisualizationDataStory from 'webapp/models-custom/visualization/data-story';
import type VisualizationFunnel from 'webapp/models-custom/visualization/funnel';
import type VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import type VisualizationHeatmap from 'webapp/models-custom/visualization/heatmap';
import type VisualizationHorizontalBarChart from 'webapp/models-custom/visualization/horizontal-bar-chart';
import type VisualizationImage from 'webapp/models-custom/visualization/image';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type VisualizationNumber from 'webapp/models-custom/visualization/number';
import type VisualizationPie from 'webapp/models-custom/visualization/pie';
import type VisualizationPipeline from 'webapp/models-custom/visualization/pipeline';
import type VisualizationProgress from 'webapp/models-custom/visualization/progress';
import type VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';
import type VisualizationSpider from 'webapp/models-custom/visualization/spider';
import type VisualizationTable from 'webapp/models-custom/visualization/table';
import type VisualizationText from 'webapp/models-custom/visualization/text';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import { FALLBACK_COLOR_INDEX } from 'webapp/d-chart/series/series';

export const MAX_ATTRIBUTE_LENGTH = 15;

// Visualization is a class that keeps all logic for a specific visualization type
// One responsibility is to define / control setup logic (e.g. which settings are available, which are not),
// and define what to do when switching to/from another visualization (e.g. cleanup, update settings)
// Another responsiblity is data logic - how to extract and group ds-data from block sample (using settings)
// and transform it into a format that can be used by either the d-chart renderer (for Highcharts-based visualizations)
// or visualization components (e.g. Block::Content::Table for Table visualization).
// This data format output is either DChartSeries or DChartPrimary/ComparePoints.
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q
// Adding a new visualization: https://databox.atlassian.net/wiki/x/FgAp3Q

export default abstract class VisualizationBase extends Serviced {
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	block: Block;

	abstract recordLimit: number;

	static recordLimit: number;

	constructor(block: Block) {
		super();
		this.block = block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	abstract visualizationType: BlockVisualization;

	@tracked forceDChartRendererType: BlockVisualizationSpecial | null = null;

	get visualizationName(): string {
		return this.mmBlocksService.getVisualizationNameFromVisualizationType(
			this.visualizationType,
		);
	}

	get dataTimezone() {
		return this.block?.dataTimezone;
	}

	get sample() {
		return this.block.sample;
	}

	// -------- visualization setup settings ---------
	// -------- (all false by default) ---------

	get hasGranulation(): boolean {
		// line, bar, heatmap
		return this.mmBlocksService.isVisualizationSeries(this.visualizationType);
		// and table under certain conditions - check model
	}

	get showGranulation(): boolean {
		return this.hasGranulation && !this.isTable;
	}

	get showCumulative(): boolean {
		return this.showGranulation && (this.isLineChart || this.isBarChart);
	}

	get canChangeVisualizationForSpecificMetric(): boolean {
		return this.isLineChart || this.isBarChart;
	}

	get canChangeColorsForSpecificMetrics(): boolean {
		return (
			this.isNumber ||
			this.isLineChart ||
			this.isBarChart ||
			this.isPie ||
			this.isRadialProgress ||
			this.isHorizontalBarChart ||
			this.isDataStory ||
			this.isBubble ||
			this.isSpider
		);
	}

	get canChangeColorsForSpecificAttributes(): boolean {
		return (
			this.isNumber ||
			this.isLineChart ||
			this.isBarChart ||
			this.isPie ||
			this.isRadialProgress ||
			this.isDataStory
		);
		// and bubble under certain conditions - check model
	}

	get allowMultipleMetricsButNotDimensions(): boolean {
		// can be multiple metrics (single output each), but no metric with multiple dimensions
		return this.isDataStory;
	}

	get allowMultipleMetricsOrDimensions(): boolean {
		return (
			this.isNumber ||
			this.isLineChart ||
			this.isBarChart ||
			this.isFunnel ||
			this.isPipeline ||
			this.isTable ||
			this.isPie ||
			this.isHorizontalBarChart ||
			this.isProgress ||
			this.isRadialProgress ||
			this.isBubble ||
			this.isSpider ||
			this.isAiSummary
		);
	}

	get allowMultipleMetrics() {
		return this.allowMultipleMetricsButNotDimensions || this.allowMultipleMetricsOrDimensions;
	}

	get maxAttributeLength() {
		return this.allowMultipleMetricsOrDimensions ? MAX_ATTRIBUTE_LENGTH : 1; // ???
	}

	get allowSingleCompareOnPrimaryDs(): boolean {
		return this.isTable || this.isProgress || this.isRadialProgress || this.isDataStory;
	}

	get allowMultipleComparesOnPrimaryDs(): boolean {
		return (
			this.isNumber ||
			this.isLineChart ||
			this.isBarChart ||
			this.isFunnel ||
			this.isPipeline ||
			this.isPie ||
			this.isGauge ||
			this.isHeatmap ||
			this.isHorizontalBarChart ||
			this.isBubble ||
			this.isSpider ||
			this.isAiSummary
		);
	}

	get allowCompareOnPrimaryDs(): boolean {
		return this.allowSingleCompareOnPrimaryDs || this.allowMultipleComparesOnPrimaryDs;
	}

	get showCompareOnPrimaryDs(): boolean {
		return this.allowCompareOnPrimaryDs && !this.showMaxValue; // bcos max value is hidden as compares
	}

	get showSortOptions(): boolean {
		return (
			this.isNumber ||
			this.isLineChart ||
			this.isBarChart ||
			this.isFunnel ||
			this.isPipeline ||
			this.isTable ||
			this.isPie ||
			this.isHorizontalBarChart
		);
	}

	get allowOtherGrouping(): boolean {
		return (
			this.isTable ||
			this.isPie ||
			this.isFunnel ||
			this.isPipeline ||
			this.isBubble ||
			this.isSpider
		);
	}

	get showLegendPosition(): boolean {
		return (
			this.isLineChart ||
			this.isBarChart ||
			this.isPie ||
			this.isBubble ||
			this.isSpider ||
			this.isFunnel ||
			this.isPipeline ||
			this.isRadialProgress
		);
		// and horizontal barchart under certain conditions - check model
	}

	get showSplineChoice(): boolean {
		return this.isLineChart;
		// and barchart under certain conditions - check model
	}

	get showStackedChoiceOnBlock(): boolean {
		return this.isHorizontalBarChart || this.isFunnel || this.isPipeline;
	}

	get showStackedChoiceOnDs(): boolean {
		return this.isLineChart || this.isBarChart;
	}

	get allowShowTotalsChoice(): boolean {
		// extra conditions apply - see block/settings: showTotalsChoice
		return this.isLineChart || this.isBarChart;
	}

	get showNumberRowChoice(): boolean {
		return this.isTable;
	}

	get showConversionSettings(): boolean {
		return this.isFunnel || this.isPipeline;
	}

	get showLabelsChoiceOnBlock(): boolean {
		return (
			this.isHeatmap || this.isPie || this.isBubble || this.isSpider || this.isRadialProgress
		);
	}

	get showLabelsChoiceOnDs(): boolean {
		return this.isLineChart || this.isBarChart || this.isHorizontalBarChart;
	}

	get showAggregationChoiceOnBlock(): boolean {
		return this.isPie;
		// and table under certain conditions - check model
	}

	get showAggregationChoiceOnDs(): boolean {
		return false;
		// table under certain conditions - check model
	}

	get showAdditionalLines(): boolean {
		return this.isLineChart || this.isBarChart;
	}

	get goalAlwaysVisible(): boolean {
		return this.isGauge;
	}

	get showMinValue(): boolean {
		return this.isProgress || this.isGauge || this.isRadialProgress;
	}

	get showMaxValue(): boolean {
		return this.isProgress || this.isGauge || this.isRadialProgress;
	}

	get allowGoalAsMaxValue(): boolean {
		return this.isProgress || this.isRadialProgress;
	}

	get allowSmallChart(): boolean {
		return this.isNumber;
	}

	get canShowAndDisableNumberOnMetric(): boolean {
		return this.isLineChart || this.isBarChart;
	}

	get canShowAndDisableNumberOnBlock(): boolean {
		return this.isGauge;
	}

	get showTableColumnType(): boolean {
		return this.isTable;
	}

	get showTableColumnInvert(): boolean {
		return (
			this.isTable && (this as VisualizationBase as VisualizationTable).isColumnsGranulations
		);
	}

	get showSortDataSelect(): boolean {
		return this.isHorizontalBarChart;
		// table under certain conditions - check model
	}

	get showYAxisSettings(): boolean {
		return this.isLineChart || this.isBarChart;
	}

	get showItemsInLineChoice(): boolean {
		return this.isHeatmap;
	}

	get maxPrimaryMainDataSelectsAllowed(): number {
		const generalMax = this.allowMultipleMetrics ? 15 : 1;
		return Math.min(this.recordLimit, generalMax); // accomodate text / image with 0
	}

	get showLabelHeader() {
		return this.isTable;
	}

	get showMediaSwitch(): boolean {
		return false; // different on Table
	}

	get showBlockRecordLimitChoice() {
		return false; // different on Table
	}

	get allowAutoUpdateOnPrimary() {
		return this.isNumber || this.isDataStory;
		// and table under certain conditions - check model
	}

	get showQuickCreateFromBoard() {
		return this.isAiSummary;
	}

	get showAiSummarySettings() {
		return this.isAiSummary;
	}

	get showFontSize(): boolean {
		const noFontSizeVisualizations = [BlockVisualization.IMAGE];
		return !noFontSizeVisualizations.includes(this.visualizationType);
	}

	get showTrafficLightsSettings(): boolean {
		return this.isGauge || this.isProgress;
	}

	get showHideTimeLeftSettings(): boolean {
		return this.isProgress;
	}

	getSortedSecondaryDataSelectsForDataSelect(dataSelect: DataSelect): DataSelect[] {
		// visualizations can implement their own sort, e.g. for line, secondary series before number
		const dataSelects = this.blockSetup.getSecondaryDataSelectsForDataSelect(dataSelect);
		return dataSelects;
	}

	getDataSelectsByColorGroups() {
		// used for determining color validity
		// e.g. since we can have same color on line and bar at the same time
		// visualization/line-chart will return two arrays
		return [this.blockSetup.dataSelects];
	}

	get enabledSortOptions(): LabelValuePairString[] {
		if (this.showSortOptions) {
			return [
				{
					value: SortFieldType.VALUE,
					label: 'Value',
				},
				{
					value: SortFieldType.NAME,
					label: 'Name',
				},
				{
					value: SortFieldType.CHANGE,
					label: 'Change',
				},
				{
					value: SortFieldType.CUSTOM,
					label: 'Custom',
				},
			];
		} else {
			return [];
		}
	}

	get enabledShownLabelsOptions() {
		return [
			{
				value: ShownLabels.VALUE,
				label: 'Value',
			},
		];
	}

	get showOnlyMainMetricDimensions(): boolean {
		const dimensionalMetricsCount = this.blockSetup.dataSelects.filter((ds) => {
			return ds.metricSetup.allowBasicDimensionValueFilters;
		}).length;
		const isMetricsColumnType = this.block.tableColumnType === TableColumnType.METRICS;

		return dimensionalMetricsCount >= 2 && isMetricsColumnType;
	}

	// ----- rendering specifics: which components to include in block::content ------

	get canRenderTopNumbers(): boolean {
		return this.isNumber || this.isGauge || this.isLineChart || this.isBarChart;
	}

	get canRenderChart(): boolean {
		return (
			this.isLineChart ||
			this.isBarChart ||
			this.isPipeline ||
			this.isFunnel ||
			this.isPie ||
			this.isHeatmap ||
			this.isGauge ||
			this.isProgress ||
			this.isRadialProgress ||
			this.isHorizontalBarChart ||
			this.isBubble ||
			this.isSpider
		);
	}

	// ----------- DATA stuff -----------

	@cached
	get statusObjects(): StatusObject[] {
		// possibly extended (for those visualizations that must set dataLocation for number & series SO)
		const statusObjects: StatusObject[] = [];
		this.blockSetup.dataSelects.forEach((ds) => {
			statusObjects.push(...ds.statusObjects);
		});
		return StatusObject.eliminateDuplicates(statusObjects, true);
	}

	@tracked numberPoints: DChartPrimaryPoint[] = []; // these are for BlockRenderStep.NUMBERS

	get isEmpty() {
		// possibly extended
		return false;
	}

	createDChartPointsFromOutput(output: DsDataOutputTopLevel) {
		return this.mmSamplesService.createDChartPointsFromOutput(output);
	}

	createDChartPointFromPoint(output: DsDataOutputBase, dsDataPoint: DsDataPointBase | null) {
		return this.mmSamplesService.createDChartPointFromPoint(output, dsDataPoint);
	}

	checkToUpdateDataCache(changes?: RenderChangesFromData) {
		const isChanged = this.checkForRenderHashChanges(changes);
		if (isChanged) {
			this.updateDataCache();
		}
	}

	updateDataCache(): void {
		// to improve performance (issue when when all autotracked props invalidates on any record push to store (bcos of allRecords / findRecord))
		// we can cache the processed data (involving sample, visualization settings etc.) on visualizations (e.g. table structure on Table block)
		// this hook will be called from block-renderer (on sampledata / visualizationSettingsHash change)
		// when not using block-renderer, must be called manually on data change wherever necessary
		// this is an optimization - for less data-heavy processing, normal getters on visualization can be used
		// possibly extended
	}

	clearDataCache(): void {
		// see updateDataCache comment for more info
		// this one sets the properties back to empty to free memory
		// called from component (block) when removed
		// possibly extended
	}

	// we count block renderer references to avoid clearing data cache too early
	// e.g. switching from wall to mobile previews, the new wrapper construct gets called before old wrapper destroy
	// and we are cleanuping data that the new renderer is using
	rendererReferenceCount = 0;

	addRendererReference() {
		this.rendererReferenceCount++;
	}

	removeRendererReference() {
		this.rendererReferenceCount = Math.max(0, this.rendererReferenceCount - 1);
		if (this.rendererReferenceCount === 0) {
			this.clearDataCache();
		}
	}

	private currentRenderHash = '';

	private checkForRenderHashChanges(changes?: RenderChangesFromData) {
		const data = this.block.sample?.sampledata;

		const renderHash = String(hashCode(JSON.stringify({ data, changes })));
		const isChanged = this.currentRenderHash !== renderHash;
		if (isChanged) {
			this.currentRenderHash = renderHash;
		}
		return isChanged;
	}

	// ----------- Detecting possible rendering changes -----------

	get visualizationSettingsHash(): number {
		// returning a number hash generated from all the visualization settings
		// that do not change the sample data returned, but does change the rendering
		// for example - tableColumnType setting
		// change of this will (through block-renderer) cause rerender in block/wrapper
		// this is extended in visualizations that have such settings
		return 0;
	}

	// ------- color schema settings --------

	getBlockThemeConfigContent(
		configs: BlockThemeConfig.Type['visualizations'],
	): BlockThemeConfig.BlockContent {
		// can be overwritten if visualizationType does not match
		const specialConfig = configs[this.visualizationType];
		return specialConfig || configs.default;
	}

	protected updateColorIndexesForItems(
		items: {
			colorIndexOrClass: string | number;
			hasNoColorIndexOrClass: boolean;
			metricSelectionData: MetricSelectionData | null;
		}[],
	) {
		const colorIndexOrder = this.blockSetup.allAvailableColorsIndexes;
		const customColorClassMap = this.blockSetup.customColorClassMap;

		let colorsLeft = colorIndexOrder;

		if (this.canChangeColorsForSpecificMetrics) {
			const usedColorIndexes: number[] = [];

			// set user-defined colors first, and mark them as used
			items.forEach((item) => {
				const metricSelectionData = item.metricSelectionData;
				if (metricSelectionData) {
					const color =
						this.blockSetup.getColorForMetricSelectionData(metricSelectionData);
					if (typeof color === 'number') {
						item.colorIndexOrClass = color;
						usedColorIndexes.push(color);
					} else if (typeof color === 'string') {
						item.colorIndexOrClass = customColorClassMap[color] || '';
					}
				}
			});

			// then, see which colors are left for free using
			colorsLeft = Array.from(colorIndexOrder).filter((c) => !usedColorIndexes.includes(c));
		}

		const itemsWithoutColor = items.filter((i) => i.hasNoColorIndexOrClass),
			dispersedColors = this.getDispersedColors(colorsLeft, itemsWithoutColor.length);

		// assign the rest of the colors
		itemsWithoutColor.forEach((item, idx) => {
			item.colorIndexOrClass = dispersedColors[idx]!;
		});
	}

	protected getDispersedColors(colorIndexes: number[], itemCount: number): number[] {
		let dispersedColorIndexes: number[] = [];

		if (itemCount > colorIndexes.length) {
			const filler = new Array(itemCount - colorIndexes.length).fill(FALLBACK_COLOR_INDEX);
			dispersedColorIndexes = Array.from(colorIndexes).concat(filler);
		} else {
			const divideInParts = itemCount - 1, // 0/x, 1/x, ... (5 items -> divide in 4 parts)
				step = divideInParts ? (colorIndexes.length - 1) / divideInParts : 0; // x, as in every x-th
			for (let i = 0; i < itemCount; i++) {
				const colorIndex = Math.round(step * i);
				dispersedColorIndexes.push(colorIndexes[colorIndex]!);
			}
		}

		// console.log({colorIndexes: Array.from(colorIndexes), itemCount, dispersedColorIndexes});
		return dispersedColorIndexes;
	}

	// ------- general helpers --------

	// when metric is changed, we sometimes need to update other settings (f.ex. horizontal bar, sortDataSelect)
	onChangeMetricInDataSelect(_dataSelect: DataSelect): void {
		return;
	}

	// when primary-main ds is created, we sometimes need to update w/e on others also
	onCreatePrimaryMainDataSelect(_dataSelect: DataSelect): void {
		return;
	}

	// when ds is deleted, we sometimes need to update w/e on others also
	onDeleteDataSelect(_dataSelect: DataSelect): void {
		return;
	}

	getTypeTextForDataSelect(_dataSelect: DataSelect): string {
		// used in advanced popup
		return 'metric';
	}

	// ------- new visualization creation helpers -------

	doAfterNewBlockCreatedActivities(): Promise<unknown> {
		// at this point, the block will be already saved and have the necessary dataSelects
		// can be extended
		return Promise.resolve();
	}

	// ------- visualization switch helpers --------

	// returns dataselects that are this visualization specific and must be deleted before switching away
	getExcessDataSelectsForSwitchTo(_visualizationTo: AnyVisualization): DataSelect[] {
		return [];
	}

	// cleanups compares, or changes from-vis specific block settings
	preCleanupForSwitchTo(_visualizationTo: AnyVisualization): void {
		return;
	}

	// adds missing stuff, creates ds, updates compares, updates block settings
	updateForSwitchFrom(_visualizationFrom: AnyVisualization): void {
		return;
	}

	// ------- Type getters -------

	get isNumber(): boolean {
		return VisualizationBase.isNumber(this);
	}

	get isLineChart(): boolean {
		return VisualizationBase.isLineChart(this);
	}

	get isBarChart(): boolean {
		return VisualizationBase.isBarChart(this);
	}

	get isFunnel(): boolean {
		return VisualizationBase.isFunnel(this);
	}

	get isPipeline(): boolean {
		return VisualizationBase.isPipeline(this);
	}

	get isTable(): boolean {
		return VisualizationBase.isTable(this);
	}

	get isProgress(): boolean {
		return VisualizationBase.isProgress(this);
	}

	get isPie(): boolean {
		return VisualizationBase.isPie(this);
	}

	get isRadialProgress(): boolean {
		return VisualizationBase.isRadialProgress(this);
	}

	get isHorizontalBarChart(): boolean {
		return VisualizationBase.isHorizontalBarChart(this);
	}

	get isDataStory(): boolean {
		return VisualizationBase.isDataStory(this);
	}

	get isHeatmap(): boolean {
		return VisualizationBase.isHeatmap(this);
	}

	get isBubble(): boolean {
		return VisualizationBase.isBubble(this);
	}

	get isSpider(): boolean {
		return VisualizationBase.isSpider(this);
	}

	get isGauge(): boolean {
		return VisualizationBase.isGauge(this);
	}

	get isText(): boolean {
		return VisualizationBase.isText(this);
	}

	get isImage(): boolean {
		return VisualizationBase.isImage(this);
	}

	get isAiSummary(): boolean {
		return VisualizationBase.isAiSummary(this);
	}

	// ------- TS type guards -------

	static isNumber(visualization: VisualizationBase): visualization is VisualizationNumber {
		return visualization.visualizationType === BlockVisualization.NUMBER;
	}

	static isLineChart(visualization: VisualizationBase): visualization is VisualizationLineChart {
		return visualization.visualizationType === BlockVisualization.LINE_CHART;
	}

	static isBarChart(visualization: VisualizationBase): visualization is VisualizationBarChart {
		return visualization.visualizationType === BlockVisualization.BAR_CHART;
	}

	static isFunnel(visualization: VisualizationBase): visualization is VisualizationFunnel {
		return visualization.visualizationType === BlockVisualization.FUNNEL;
	}

	static isPipeline(visualization: VisualizationBase): visualization is VisualizationPipeline {
		return visualization.visualizationType === BlockVisualization.PIPELINE;
	}

	static isTable(visualization: VisualizationBase): visualization is VisualizationTable {
		return visualization.visualizationType === BlockVisualization.TABLE;
	}

	static isProgress(visualization: VisualizationBase): visualization is VisualizationProgress {
		return visualization.visualizationType === BlockVisualization.PROGRESS;
	}

	static isPie(visualization: VisualizationBase): visualization is VisualizationPie {
		return visualization.visualizationType === BlockVisualization.PIE;
	}

	static isRadialProgress(
		visualization: VisualizationBase,
	): visualization is VisualizationRadialProgress {
		return visualization.visualizationType === BlockVisualization.RADIAL_PROGRESS;
	}

	static isHorizontalBarChart(
		visualization: VisualizationBase,
	): visualization is VisualizationHorizontalBarChart {
		return visualization.visualizationType === BlockVisualization.HORIZONTAL_BAR_CHART;
	}

	static isDataStory(visualization: VisualizationBase): visualization is VisualizationDataStory {
		return visualization.visualizationType === BlockVisualization.DATA_STORY;
	}

	static isHeatmap(visualization: VisualizationBase): visualization is VisualizationHeatmap {
		return visualization.visualizationType === BlockVisualization.HEATMAP;
	}

	static isBubble(visualization: VisualizationBase): visualization is VisualizationBubble {
		return visualization.visualizationType === BlockVisualization.BUBBLE;
	}

	static isSpider(visualization: VisualizationBase): visualization is VisualizationSpider {
		return visualization.visualizationType === BlockVisualization.SPIDER;
	}

	static isGauge(visualization: VisualizationBase): visualization is VisualizationGauge {
		return visualization.visualizationType === BlockVisualization.GAUGE;
	}

	static isText(visualization: VisualizationBase): visualization is VisualizationText {
		return visualization.visualizationType === BlockVisualization.TEXT;
	}

	static isImage(visualization: VisualizationBase): visualization is VisualizationImage {
		return visualization.visualizationType === BlockVisualization.IMAGE;
	}

	static isAiSummary(visualization: VisualizationBase): visualization is VisualizationAiSummary {
		return visualization.visualizationType === BlockVisualization.AI_SUMMARY;
	}

	// ------- MISC --------
	getAllMainPoints(): DChartPrimaryPoint[] {
		// Will be overriden on extend. Currently only used for Metric Insights
		return [];
	}
}
